// RankingsTab — Classifiche clienti su follower, views, engagement e Meta Ads.
// Dati recuperati da Zernio via /api/rankings/overview.
import { useEffect, useState, useMemo } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Trophy, TrendingUp, Eye, Heart, Target, Loader2, Calendar as CalendarIcon, CalendarDays, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../../utils/api";
import { formatNumber } from "./common";

const MEDAL = ["bg-yellow-400 text-yellow-900", "bg-slate-300 text-slate-700", "bg-orange-300 text-orange-900"];

function RankingCard({ title, icon, color, items, valueFormat, unit, emptyMsg, testid }) {
  const top = items.slice(0, 20);
  return (
    <Card className={`p-4 border ${color.border} ${color.bg}`} data-testid={`ranking-card-${testid}`}>
      <div className={`flex items-center gap-2 mb-3 ${color.text}`}>
        {icon}
        <h3 className="font-semibold text-sm">{title}</h3>
        <span className="text-[11px] opacity-70 ml-auto">{items.length} clienti</span>
      </div>
      {top.length === 0 ? (
        <div className="text-xs text-slate-500 py-8 text-center">{emptyMsg}</div>
      ) : (
        <ol className="space-y-2.5 max-h-[520px] overflow-auto pr-1">
          {top.map((r, idx) => (
            <li
              key={r.client_id}
              className="flex items-center gap-3 text-sm py-2 px-3 rounded-lg border border-white/70 bg-white/45 shadow-sm hover:bg-white hover:shadow-md transition-all duration-200"
              data-testid={`ranking-${testid}-item-${idx}`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[11px] flex-shrink-0 ${idx < 3 ? MEDAL[idx] : "bg-white text-slate-500 border border-slate-200"}`}>
                {idx + 1}
              </span>
              <span className="flex-1 truncate text-slate-700 font-medium" title={r.name}>{r.name}</span>
              <span className="font-bold text-slate-900 whitespace-nowrap font-mono text-sm">
                {valueFormat(r)}{unit || ""}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// Helpers date
function toISO(d) { return d.toISOString().slice(0, 10); }
function startOfWeek(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Lunedì = 0
  x.setDate(x.getDate() - dow);
  return x;
}
function endOfWeek(d) {
  const s = startOfWeek(d);
  s.setDate(s.getDate() + 6);
  return s;
}
function startOfMonth(d) { const x = new Date(d); x.setDate(1); return x; }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); return x; }

function formatEuro(v) {
  return "€" + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function RankingsTab() {
  const today = useMemo(() => new Date(), []);
  const [mode, setMode] = useState("week");   // "week" | "month" | "custom"
  const [weekAnchor, setWeekAnchor] = useState(toISO(today));   // qualsiasi giorno nella settimana
  const [monthAnchor, setMonthAnchor] = useState(toISO(today).slice(0, 7)); // YYYY-MM
  const [customFrom, setCustomFrom] = useState(toISO(today));
  const [customTo, setCustomTo] = useState(toISO(today));

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Calcola window effettiva
  const window = useMemo(() => {
    if (mode === "week") {
      const anchor = new Date(weekAnchor);
      return { from: toISO(startOfWeek(anchor)), to: toISO(endOfWeek(anchor)) };
    }
    if (mode === "month") {
      const [y, m] = monthAnchor.split("-").map(Number);
      const anchor = new Date(y, m - 1, 15);
      return { from: toISO(startOfMonth(anchor)), to: toISO(endOfMonth(anchor)) };
    }
    return { from: customFrom, to: customTo };
  }, [mode, weekAnchor, monthAnchor, customFrom, customTo]);

  const isMonthlyView = mode === "month" || (
    mode === "custom" && (() => {
      const d1 = new Date(customFrom); const d2 = new Date(customTo);
      return (d2 - d1) / 86400000 >= 28;
    })()
  );

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`/rankings/overview?from=${window.from}&to=${window.to}`);
        if (!r.ok) { toast.error("Errore caricamento classifiche"); return; }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        toast.error("Errore: " + (e.message || ""));
      } finally { if (!cancelled) setLoading(false); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [window.from, window.to]);

  return (
    <div className="space-y-4">
      {/* Header con selettore */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-semibold">Classifiche clienti</h2>
          <div className="text-[11px] sm:text-xs text-muted-foreground ml-auto">
            <strong className="text-slate-700">{window.from}</strong> → <strong className="text-slate-700">{window.to}</strong>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle tra Settimana/Mese/Custom */}
          <div className="flex gap-1 bg-slate-100 rounded-md p-1">
            <Button
              variant={mode === "week" ? "default" : "ghost"}
              size="sm"
              className={`h-7 px-3 text-xs ${mode === "week" ? "bg-white shadow" : ""}`}
              onClick={() => setMode("week")}
              data-testid="rankings-mode-week"
            >
              <CalendarDays className="w-3.5 h-3.5 mr-1" /> Settimana
            </Button>
            <Button
              variant={mode === "month" ? "default" : "ghost"}
              size="sm"
              className={`h-7 px-3 text-xs ${mode === "month" ? "bg-white shadow" : ""}`}
              onClick={() => setMode("month")}
              data-testid="rankings-mode-month"
            >
              <CalendarIcon className="w-3.5 h-3.5 mr-1" /> Mese
            </Button>
            <Button
              variant={mode === "custom" ? "default" : "ghost"}
              size="sm"
              className={`h-7 px-3 text-xs ${mode === "custom" ? "bg-white shadow" : ""}`}
              onClick={() => setMode("custom")}
              data-testid="rankings-mode-custom"
            >
              <CalendarRange className="w-3.5 h-3.5 mr-1" /> Personalizzato
            </Button>
          </div>

          {/* Input in base al mode */}
          {mode === "week" && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-500">Settimana di:</span>
              <Input
                type="date"
                value={weekAnchor}
                onChange={(e) => setWeekAnchor(e.target.value)}
                className="w-40 h-8 text-xs"
                data-testid="rankings-week-picker"
              />
            </div>
          )}
          {mode === "month" && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-500">Mese:</span>
              <Input
                type="month"
                value={monthAnchor}
                onChange={(e) => setMonthAnchor(e.target.value)}
                className="w-40 h-8 text-xs"
                data-testid="rankings-month-picker"
              />
            </div>
          )}
          {mode === "custom" && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-slate-500">Dal:</span>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-40 h-8 text-xs"
                data-testid="rankings-custom-from"
              />
              <span className="text-xs text-slate-500">Al:</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-40 h-8 text-xs"
                data-testid="rankings-custom-to"
              />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento classifiche...
        </Card>
      ) : !data ? (
        <Card className="p-12 text-center text-muted-foreground">Nessun dato disponibile</Card>
      ) : (
        <>
          {/* Prima riga: Social */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <RankingCard
              title="Crescita Follower"
              icon={<TrendingUp className="w-4 h-4" />}
              color={{ border: "border-emerald-200", bg: "bg-emerald-50/50", text: "text-emerald-700" }}
              items={data.follower_growth}
              valueFormat={(r) => (r.follower_growth >= 0 ? "+" : "") + formatNumber(r.follower_growth)}
              emptyMsg="Nessun dato follower nel periodo"
              testid="follower"
            />
            <RankingCard
              title="Views Totali"
              icon={<Eye className="w-4 h-4" />}
              color={{ border: "border-fuchsia-200", bg: "bg-fuchsia-50/50", text: "text-fuchsia-700" }}
              items={data.views}
              valueFormat={(r) => formatNumber(r.views)}
              emptyMsg="Nessuna view registrata"
              testid="views"
            />
            <RankingCard
              title="Engagement Rate"
              icon={<Heart className="w-4 h-4" />}
              color={{ border: "border-rose-200", bg: "bg-rose-50/50", text: "text-rose-700" }}
              items={data.engagement}
              valueFormat={(r) => (r.engagement != null ? r.engagement.toFixed(2) : "-")}
              unit="%"
              emptyMsg="Nessun dato engagement"
              testid="engagement"
            />
          </div>

          {/* Seconda riga: Meta Ads */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <RankingCard
              title="Meta Ads - N° Appuntamenti"
              icon={<Target className="w-4 h-4" />}
              color={{ border: "border-blue-200", bg: "bg-blue-50/50", text: "text-blue-700" }}
              items={data.ads_appointments || []}
              valueFormat={(r) => formatNumber(r.ads_appointments)}
              emptyMsg="Nessun appuntamento registrato nel periodo"
              testid="ads-appointments"
            />
            <RankingCard
              title="Meta Ads - % Conversione Lead → Appuntamento"
              icon={<Target className="w-4 h-4" />}
              color={{ border: "border-indigo-200", bg: "bg-indigo-50/50", text: "text-indigo-700" }}
              items={data.ads_conversion}
              valueFormat={(r) => (r.ads_conversion_pct != null ? r.ads_conversion_pct.toFixed(1) : "-")}
              unit="%"
              emptyMsg="Nessuna campagna con lead nel periodo"
              testid="ads-conversion"
            />
            {isMonthlyView ? (
              <RankingCard
                title="Meta Ads - Fatturato Raggiunto"
                icon={<Target className="w-4 h-4" />}
                color={{ border: "border-amber-200", bg: "bg-amber-50/50", text: "text-amber-700" }}
                items={data.ads_revenue || []}
                valueFormat={(r) => formatEuro(r.ads_revenue)}
                emptyMsg="Nessun fatturato registrato nel periodo"
                testid="ads-revenue"
              />
            ) : (
              <Card className="p-4 border border-dashed border-amber-200 bg-amber-50/30 flex items-center justify-center text-center" data-testid="ads-revenue-disabled">
                <div className="text-amber-800/80 text-xs">
                  <Target className="w-5 h-5 mx-auto mb-2 opacity-60" />
                  <p className="font-semibold">Fatturato Meta Ads</p>
                  <p className="mt-1">Disponibile solo per intervalli mensili.<br />Passa a <b>Mese</b> per visualizzarla.</p>
                </div>
              </Card>
            )}
          </div>

          <div className="text-[11px] text-slate-500 px-2">
            Nota: le classifiche mostrano tutti i clienti con dati disponibili per la metrica selezionata,
            ordinati dal migliore al peggiore. Il fatturato Meta Ads è la somma dei valori inseriti manualmente
            nella pagina Social per ciascuna settimana del periodo.
          </div>
        </>
      )}
    </div>
  );
}
